import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { 
  ChevronLeft, ChevronRight, TrendingUp, Zap, Building, Bitcoin, 
  Cpu, DollarSign, Trophy, FlaskConical, BarChart3, Globe, 
  Theater, Star, Heart, Music, Map, ChevronDown, Search, X, Calendar
} from 'lucide-react';
import { Input } from './ui/input';
import { apiService } from '../services/api';

// Icon mapping for categories
const ICON_MAP = {
  'trending-up': TrendingUp,
  'zap': Zap,
  'building': Building,
  'bitcoin': Bitcoin,
  'cpu': Cpu,
  'dollar-sign': DollarSign,
  'trophy': Trophy,
  'flask': FlaskConical,
  'bar-chart': BarChart3,
  'globe': Globe,
  'theater': Theater,
  'star': Star,
  'heart': Heart,
  'music': Music,
  'map': Map,
};

// Fallback categories (used if API fails)
const FALLBACK_CATEGORIES = [
  { id: 'all', name: 'All', icon: 'bar-chart', subcategories: [] },
  { id: 'trending', name: 'Trending', icon: 'trending-up', subcategories: [] },
  { id: 'right_now', name: 'Right Now', icon: 'zap', subcategories: [] },
  { id: 'markets', name: 'Markets', icon: 'trending-up', subcategories: ['us_markets', 'global_markets', 'commodities', 'forex'] },
  { id: 'crypto', name: 'Crypto', icon: 'bitcoin', subcategories: ['bitcoin', 'ethereum', 'altcoins', 'defi', 'nfts'] },
  { id: 'tech', name: 'Tech', icon: 'cpu', subcategories: ['ai_ml', 'big_tech', 'gaming', 'social_media'] },
  { id: 'finance', name: 'Finance', icon: 'dollar-sign', subcategories: ['federal_reserve', 'banking', 'interest_rates', 'inflation'] },
  { id: 'sports', name: 'Sports', icon: 'trophy', subcategories: ['football_soccer', 'nfl', 'nba', 'mlb', 'nhl', 'tennis', 'golf', 'mma_ufc', 'boxing', 'cricket', 'rugby', 'formula_1', 'olympics', 'esports'] },
  { id: 'science', name: 'Science', icon: 'flask', subcategories: ['space', 'climate', 'medicine', 'physics'] },
  { id: 'economics', name: 'Economics', icon: 'bar-chart', subcategories: ['gdp', 'employment', 'trade', 'recession'] },
  { id: 'world_events', name: 'World Events', icon: 'globe', subcategories: ['elections', 'geopolitics', 'conflicts', 'diplomacy'] },
  { id: 'culture', name: 'Culture', icon: 'film', subcategories: ['movies', 'tv_shows', 'music', 'awards'] },
  { id: 'pop_culture', name: 'Pop Culture', icon: 'star', subcategories: ['celebrities', 'viral_trends', 'social_media', 'memes'] },
  { id: 'black_culture', name: 'Black Culture', icon: 'users', subcategories: ['entertainment', 'sports', 'business', 'politics'] },
  { id: 'urban_culture', name: 'Urban Culture', icon: 'music', subcategories: ['hip_hop', 'fashion', 'streetwear', 'sneakers'] },
  { id: 'continents', name: 'Continents', icon: 'map', subcategories: ['north_america', 'south_america', 'europe', 'asia', 'africa', 'oceania', 'middle_east'] },
  { id: 'resolved', name: 'Resolved', icon: 'check-circle', subcategories: [] }
];

// Dropdown Portal Component
const DropdownPortal = ({ children, buttonRef, isOpen }) => {
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 4,
        left: rect.left
      });
    }
  }, [isOpen, buttonRef]);

  if (!isOpen) return null;

  return createPortal(
    <div 
      style={{ 
        position: 'fixed', 
        top: position.top, 
        left: position.left,
        zIndex: 9999 
      }}
    >
      {children}
    </div>,
    document.body
  );
};

const CategoryBar = ({ selectedCategory, onSelectCategory, closingDateFilter = 'all', onClosingDateChange }) => {
  const scrollRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [categories, setCategories] = useState(FALLBACK_CATEGORIES);
  const [showDateDropdown, setShowDateDropdown] = useState(false);
  const buttonRefs = useRef({});
  const dateButtonRef = useRef(null);
  const navigate = useNavigate();

  const closingDateOptions = [
    { id: 'all', label: 'All Dates' },
    { id: 'today', label: 'Closing Today' },
    { id: 'tomorrow', label: 'Closing Tomorrow' },
    { id: 'this-week', label: 'This Week' },
    { id: 'this-month', label: 'This Month' },
    { id: 'next-month', label: 'Next Month' },
  ];

  // Fetch categories from backend
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await apiService.getCategories();
        if (response?.categories) {
          // Add "All" at the beginning, filter out duplicates
          const categoryIds = new Set();
          const allCategories = [
            { id: 'all', name: 'All', icon: 'bar-chart', subcategories: [] }
          ];
          categoryIds.add('all');
          
          response.categories.forEach(cat => {
            if (!categoryIds.has(cat.id)) {
              categoryIds.add(cat.id);
              allCategories.push(cat);
            }
          });
          
          setCategories(allCategories);
        }
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };
    
    fetchCategories();
  }, []);

  // Reset subcategory when main category changes
  useEffect(() => {
    setSelectedSubcategory(null);
  }, [selectedCategory]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (expandedCategory && !e.target.closest('[data-dropdown]')) {
        setExpandedCategory(null);
      }
      if (showDateDropdown && !e.target.closest('[data-date-dropdown]')) {
        setShowDateDropdown(false);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [expandedCategory, showDateDropdown]);

  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, [categories]);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = direction === 'left' ? -200 : 200;
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      setTimeout(checkScroll, 300);
    }
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults(null);
      return;
    }

    setSearching(true);
    try {
      const results = await apiService.globalSearch(query);
      setSearchResults(results);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setSearching(false);
    }
  };

  const handleCategoryClick = (e, category) => {
    e.stopPropagation();
    if (category.subcategories && category.subcategories.length > 0) {
      setExpandedCategory(expandedCategory === category.id ? null : category.id);
    } else {
      onSelectCategory(category.id);
      setExpandedCategory(null);
    }
  };

  const handleSubcategoryClick = (subcategoryId, subcategoryName) => {
    setSelectedSubcategory({ id: subcategoryId, name: subcategoryName });
    onSelectCategory(subcategoryId);
    setExpandedCategory(null);
  };

  // Get icon component from icon string
  const getIconComponent = (iconName) => {
    return ICON_MAP[iconName] || BarChart3;
  };

  return (
    <div className="sticky top-0 z-40 bg-[#050810] border-b border-[#1e2a45]">
      {/* Search Bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
        <div className="relative">
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
              <Input
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Search markets, categories, predictions..."
                className="pl-10 pr-10 bg-[#131b2e] border-[#1e2a45] text-white placeholder:text-gray-500 focus:border-[#3B82F6]"
                data-testid="global-search-input"
              />
              {searchQuery && (
                <button
                  onClick={() => { setSearchQuery(''); setSearchResults(null); }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* Search Results Dropdown */}
          {searchResults && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-[#131b2e] border border-[#1e2a45] rounded-xl shadow-xl max-h-96 overflow-y-auto z-50">
              {searchResults.total_count === 0 ? (
                <div className="p-4 text-center text-gray-400">
                  No results found for "{searchQuery}"
                </div>
              ) : (
                <div className="p-2">
                  {/* Categories */}
                  {searchResults.categories?.length > 0 && (
                    <div className="mb-3">
                      <h4 className="text-xs font-semibold text-gray-500 px-2 mb-1">CATEGORIES</h4>
                      {searchResults.categories.map((cat) => (
                        <button
                          key={cat.id}
                          onClick={() => {
                            onSelectCategory(cat.id);
                            setSearchQuery('');
                            setSearchResults(null);
                          }}
                          className="w-full text-left px-3 py-2 rounded-lg hover:bg-[#1e2a45] text-white text-sm"
                        >
                          {cat.name}
                          {cat.parent_name && (
                            <span className="text-gray-500 ml-2">in {cat.parent_name}</span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Markets */}
                  {searchResults.markets?.length > 0 && (
                    <div className="mb-3">
                      <h4 className="text-xs font-semibold text-gray-500 px-2 mb-1">MARKETS</h4>
                      {searchResults.markets.slice(0, 5).map((market) => (
                        <button
                          key={market.id}
                          onClick={() => {
                            navigate(`/?market=${market.id}`);
                            setSearchQuery('');
                            setSearchResults(null);
                          }}
                          className="w-full text-left px-3 py-2 rounded-lg hover:bg-[#1e2a45]"
                        >
                          <span className="text-white text-sm block truncate">{market.title}</span>
                          <span className="text-gray-500 text-xs">{market.category}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Ideas */}
                  {searchResults.ideas?.length > 0 && (
                    <div>
                      <h4 className="text-xs font-semibold text-gray-500 px-2 mb-1">IDEAS</h4>
                      {searchResults.ideas.slice(0, 5).map((idea) => (
                        <button
                          key={idea.id}
                          onClick={() => {
                            navigate('/ideas');
                            setSearchQuery('');
                            setSearchResults(null);
                          }}
                          className="w-full text-left px-3 py-2 rounded-lg hover:bg-[#1e2a45]"
                        >
                          <span className="text-white text-sm block truncate">{idea.title}</span>
                          <span className="text-gray-500 text-xs">{idea.category}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Category Tabs */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between">
          {/* Categories with scroll */}
          <div className="relative flex-1 min-w-0">
            {/* Left Arrow */}
            {showLeftArrow && (
              <button
                onClick={() => scroll('left')}
                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-gradient-to-r from-[#050810] via-[#050810] to-transparent pl-2 pr-6 py-4"
              >
                <ChevronLeft className="h-5 w-5 text-gray-400 hover:text-white transition-colors" />
              </button>
            )}

            {/* Categories */}
            <div
              ref={scrollRef}
              onScroll={checkScroll}
              className="flex items-center space-x-1 overflow-x-auto scrollbar-hide py-2"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {categories.map((category) => {
                const Icon = getIconComponent(category.icon);
                const isSelected = selectedCategory === category.id;
                const isExpanded = expandedCategory === category.id;
                const hasSubcategories = category.subcategories && category.subcategories.length > 0;
                
                // Check if a subcategory of this category is selected
                const selectedSub = hasSubcategories && category.subcategories.find(s => s.id === selectedCategory);
                const showAsSelected = isSelected || selectedSub;

                // Create ref for this button
                if (!buttonRefs.current[category.id]) {
                  buttonRefs.current[category.id] = React.createRef();
                }

                return (
                  <div key={category.id} className="relative" data-dropdown>
                    <button
                      ref={buttonRefs.current[category.id]}
                      onClick={(e) => handleCategoryClick(e, category)}
                      data-testid={`category-${category.id}`}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                        showAsSelected
                          ? 'bg-[#3B82F6] text-white shadow-lg shadow-[#3B82F6]/20'
                          : 'text-gray-400 hover:text-white hover:bg-[#1e2a45]'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      {/* Show subcategory name if selected, otherwise show category name */}
                      {selectedSub ? selectedSub.name : category.name}
                      {hasSubcategories && (
                        <ChevronDown className={`h-3 w-3 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                      )}
                    </button>

                    {/* Subcategories Dropdown - Using Portal */}
                    <DropdownPortal 
                      buttonRef={buttonRefs.current[category.id]} 
                      isOpen={isExpanded && hasSubcategories}
                    >
                      <div 
                        className="bg-[#131b2e] border border-[#1e2a45] rounded-xl shadow-2xl py-2 min-w-[200px] max-h-[400px] overflow-y-auto"
                        data-dropdown
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          onClick={() => {
                            onSelectCategory(category.id);
                            setExpandedCategory(null);
                          }}
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-[#1e2a45] hover:text-white ${
                            isSelected && !selectedSub ? 'text-[#3B82F6] font-medium' : 'text-gray-300'
                          }`}
                        >
                          All {category.name}
                        </button>
                        <div className="border-t border-[#1e2a45] my-1" />
                        {category.subcategories.map((sub) => (
                          <button
                            key={sub.id}
                            onClick={() => handleSubcategoryClick(sub.id, sub.name)}
                            className={`w-full text-left px-4 py-2 text-sm hover:bg-[#1e2a45] hover:text-white ${
                              selectedCategory === sub.id ? 'text-[#3B82F6] font-medium' : 'text-gray-300'
                            }`}
                          >
                            {sub.name}
                          </button>
                        ))}
                      </div>
                    </DropdownPortal>
                  </div>
                );
              })}
            </div>

            {/* Right Arrow */}
            {showRightArrow && (
              <button
                onClick={() => scroll('right')}
                className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-gradient-to-l from-[#050810] via-[#050810] to-transparent pr-2 pl-6 py-4"
              >
                <ChevronRight className="h-5 w-5 text-gray-400 hover:text-white transition-colors" />
              </button>
            )}
          </div>

          {/* Closing Date Filter - Always visible */}
          {onClosingDateChange && (
            <div className="relative ml-4 flex-shrink-0" data-date-dropdown>
              <button
                ref={dateButtonRef}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDateDropdown(!showDateDropdown);
                }}
                data-testid="closing-date-filter"
                className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all border ${
                  closingDateFilter !== 'all'
                    ? 'bg-[#3B82F6]/10 border-[#3B82F6] text-[#3B82F6]'
                    : 'border-[#1e2a45] text-gray-400 hover:text-white hover:bg-[#1e2a45]'
                }`}
              >
                <Calendar className="h-4 w-4" />
                <span>{closingDateOptions.find(o => o.id === closingDateFilter)?.label}</span>
                <ChevronDown className={`h-3 w-3 transition-transform ${showDateDropdown ? 'rotate-180' : ''}`} />
              </button>
              
              {showDateDropdown && (
                <div 
                  className="absolute right-0 mt-2 w-48 bg-[#131b2e] border border-[#1e2a45] rounded-xl shadow-xl z-50 overflow-hidden"
                  onClick={(e) => e.stopPropagation()}
                >
                  {closingDateOptions.map((option) => (
                    <button
                      key={option.id}
                      onClick={() => {
                        onClosingDateChange(option.id);
                        setShowDateDropdown(false);
                      }}
                      className={`w-full px-4 py-3 text-left text-sm hover:bg-[#1e2a45] transition-colors flex items-center justify-between ${
                        closingDateFilter === option.id 
                          ? 'bg-[#3B82F6]/10 text-[#3B82F6]' 
                          : 'text-gray-300'
                      }`}
                      data-testid={`date-filter-${option.id}`}
                    >
                      {option.label}
                      {closingDateFilter === option.id && (
                        <span className="w-2 h-2 bg-[#3B82F6] rounded-full"></span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CategoryBar;
